import { useState, useRef, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { isToday, isTomorrow, isPeriodPastToday } from '../components/WhenDropdown/whenDropdownUtils'
import { parseStartDate } from '../utils/parseStartDate'
import type { StartPeriod } from '@/src/renderer/lib/db/models'

export interface DateTimeValue {
  date: Date
  time: string | null
  period: StartPeriod | null
}

interface UseDateTimeDropdownStateOptions {
  value: DateTimeValue | null
  onChange: (value: DateTimeValue) => void
  onClose: () => void
  /** Format the current draft state for the NL input display */
  formatDisplay: (value: DateTimeValue, locale: string) => string
}

/**
 * Shared state management for date/time dropdown components (WhenDropdown, DeadlineDropdown).
 *
 * Encapsulates:
 * - Draft state (selectedDate, selectedPeriod, selectedTime, daySource, timeSource)
 * - Day quick-pick / calendar selection
 * - Period selection with auto-date and toggle
 * - Time value application and clearing
 * - Confirm logic
 * - NL input parsing and keyboard shortcuts (Cmd+1-6, Enter, Escape)
 * - inputValue management for the NL display field
 */
export function useDateTimeDropdownState({
  value,
  onChange,
  onClose,
  formatDisplay,
}: UseDateTimeDropdownStateOptions) {
  const { i18n } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)

  // Focus the NL input on mount. autoFocus is unreliable in floating-ui portals
  // because the portal content starts as visibility:hidden while positioning runs.
  useEffect(() => {
    const timer = setTimeout(() => { inputRef.current?.focus() }, 50)
    return () => clearTimeout(timer)
  }, [])

  // ── Internal draft state ──
  const [selectedDate, setSelectedDate] = useState<Date | null>(value?.date ?? null)
  const [selectedPeriod, setSelectedPeriod] = useState<StartPeriod | null>(value?.period ?? null)
  const [selectedTime, setSelectedTime] = useState<string>(value?.time ?? '')
  const [daySource, setDaySource] = useState<'quick' | 'calendar' | null>(null)
  const [timeSource, setTimeSource] = useState<'period' | 'time' | null>(
    value?.period ? 'period' : value?.time ? 'time' : null
  )
  const [inputValue, setInputValue] = useState<string | null>(null)

  // Initialize daySource from existing value
  useEffect(() => {
    if (value?.date && !value?.period) {
      if (isToday(value.date)) setDaySource('quick')
      else if (isTomorrow(value.date)) setDaySource('quick')
      else setDaySource('calendar')
    } else if (value?.date && value?.period) {
      setDaySource(null)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const hasSelection = selectedDate !== null

  // ── Display text for NL input ──
  const displayText = (() => {
    if (!selectedDate) return ''
    return formatDisplay(
      { date: selectedDate, time: selectedTime || null, period: selectedPeriod },
      i18n.language,
    )
  })()

  // ── Day selection ──
  const handleQuickDay = useCallback((offset: number) => {
    const d = new Date()
    d.setDate(d.getDate() + offset)
    d.setHours(0, 0, 0, 0)
    setSelectedDate(d)
    setDaySource('quick')
    setSelectedPeriod(null)
    setSelectedTime('')
    setTimeSource(null)
    setInputValue(null)
  }, [])

  const handleCalendarDay = useCallback((date: Date) => {
    setSelectedDate(date)
    setDaySource('calendar')
    setInputValue(null)
  }, [])

  // ── Period selection ──
  const handlePeriodSelect = useCallback((period: StartPeriod) => {
    if (selectedPeriod === period && timeSource === 'period') {
      setSelectedPeriod(null)
      setTimeSource(null)
      setInputValue(null)
      if (selectedDate && (isToday(selectedDate) || isTomorrow(selectedDate))) {
        setDaySource('quick')
      }
      return
    }

    setSelectedPeriod(period)
    setSelectedTime('')
    setTimeSource('period')
    setInputValue(null)

    const shouldUseTomorrow = isPeriodPastToday(period) && (!selectedDate || isToday(selectedDate))
    if (!selectedDate || shouldUseTomorrow) {
      const d = new Date()
      if (shouldUseTomorrow) d.setDate(d.getDate() + 1)
      d.setHours(0, 0, 0, 0)
      setSelectedDate(d)
    }
    if (daySource === 'quick') {
      setDaySource(null)
    }
  }, [selectedDate, daySource, selectedPeriod, timeSource])

  // ── Time value application ──
  const applyTimeValue = useCallback((val: string) => {
    if (val) {
      setSelectedPeriod(null)
      setTimeSource('time')
      setInputValue(null)
      if (!selectedDate) {
        const d = new Date()
        d.setHours(0, 0, 0, 0)
        setSelectedDate(d)
      }
    } else {
      setTimeSource(null)
      setInputValue(null)
    }
  }, [selectedDate])

  const handleTimeClear = useCallback(() => {
    setSelectedTime('')
    setTimeSource(selectedPeriod ? 'period' : null)
    setInputValue(null)
    setTimeout(() => { inputRef.current?.focus() }, 50)
  }, [selectedPeriod])

  // ── Confirm ──
  const handleConfirm = useCallback(() => {
    if (!selectedDate) return
    onChange({
      date: selectedDate,
      time: selectedTime || null,
      period: selectedPeriod,
    })
    onClose()
  }, [selectedDate, selectedTime, selectedPeriod, onChange, onClose])

  // ── Shortcut helper ──
  const shortcutAndReset = useCallback((fn: () => void) => {
    fn()
    setInputValue(null)
  }, [])

  // ── NL input keyboard ──
  const handleInputKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation()

    if (e.key === 'Enter') {
      e.preventDefault()
      if (inputValue && inputValue.trim()) {
        const parsed = parseStartDate(inputValue, i18n.language)
        if (parsed) {
          onChange({
            date: parsed.date,
            time: parsed.time ?? null,
            period: parsed.period,
          })
          onClose()
        }
      } else if (hasSelection) {
        handleConfirm()
      }
      return
    }

    if (e.key === 'Escape') {
      e.preventDefault()
      onClose()
      return
    }

    if (e.metaKey || e.ctrlKey) {
      if (e.key === '1') { e.preventDefault(); shortcutAndReset(() => handleQuickDay(0)); return }
      if (e.key === '2') { e.preventDefault(); shortcutAndReset(() => handleQuickDay(1)); return }
      if (e.key === '3') { e.preventDefault(); shortcutAndReset(() => handlePeriodSelect('morning')); return }
      if (e.key === '4') { e.preventDefault(); shortcutAndReset(() => handlePeriodSelect('noon')); return }
      if (e.key === '5') { e.preventDefault(); shortcutAndReset(() => handlePeriodSelect('afternoon')); return }
      if (e.key === '6') { e.preventDefault(); shortcutAndReset(() => handlePeriodSelect('evening')); return }
    }
  }, [inputValue, i18n.language, hasSelection, onClose, handleQuickDay, handlePeriodSelect, handleConfirm, shortcutAndReset, onChange])

  // ── Global keyboard ──
  const handleGlobalKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.metaKey || e.ctrlKey) {
      if (e.key === '1') { e.preventDefault(); shortcutAndReset(() => handleQuickDay(0)); return }
      if (e.key === '2') { e.preventDefault(); shortcutAndReset(() => handleQuickDay(1)); return }
      if (e.key === '3') { e.preventDefault(); shortcutAndReset(() => handlePeriodSelect('morning')); return }
      if (e.key === '4') { e.preventDefault(); shortcutAndReset(() => handlePeriodSelect('noon')); return }
      if (e.key === '5') { e.preventDefault(); shortcutAndReset(() => handlePeriodSelect('afternoon')); return }
      if (e.key === '6') { e.preventDefault(); shortcutAndReset(() => handlePeriodSelect('evening')); return }
    }
    if (e.key === 'Enter' && hasSelection) {
      const target = e.target as HTMLElement
      if (target === inputRef.current) return
      e.preventDefault()
      handleConfirm()
    }
  }, [hasSelection, handleConfirm, handleQuickDay, handlePeriodSelect, shortcutAndReset])

  return {
    inputRef,
    selectedDate,
    selectedPeriod,
    selectedTime,
    setSelectedTime,
    daySource,
    timeSource,
    inputValue,
    setInputValue,
    hasSelection,
    displayText,
    handleQuickDay,
    handleCalendarDay,
    handlePeriodSelect,
    applyTimeValue,
    handleTimeClear,
    handleConfirm,
    handleInputKeyDown,
    handleGlobalKeyDown,
  }
}
